/**
 * Document review, sign-off and e-signature over gov-api.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DecideSignoffDto,
  DocumentSignoffView,
  FileSignoffs,
  SignedDocumentVerification,
  SignoffQueue,
  StoredSignature,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { formsKeys } from './forms-api';

export const signoffKeys = {
  file: (fileId: string) => ['signoffs', 'file', fileId] as const,
  queue: ['signoffs', 'queue'] as const,
  mySignature: ['signoffs', 'my-signature'] as const,
};

export async function getFileSignoffs(fileId: string): Promise<FileSignoffs> {
  const { data } = await api.get<FileSignoffs>(`/files/${fileId}/signoffs`);
  return data;
}

export async function getSignoffQueue(): Promise<SignoffQueue> {
  const { data } = await api.get<SignoffQueue>('/signoffs/queue');
  return data;
}

export async function submitSignoff(
  fileId: string,
  formId: string,
  note?: string,
): Promise<DocumentSignoffView> {
  const { data } = await api.post<DocumentSignoffView>(
    `/files/${fileId}/forms/${formId}/signoff`,
    note ? { note } : {},
  );
  return data;
}

export async function decideSignoff(
  fileId: string,
  id: string,
  dto: DecideSignoffDto,
): Promise<DocumentSignoffView> {
  const { data } = await api.post<DocumentSignoffView>(
    `/files/${fileId}/signoffs/${id}/decision`,
    dto,
  );
  return data;
}

export async function withdrawSignoff(fileId: string, id: string): Promise<DocumentSignoffView> {
  const { data } = await api.post<DocumentSignoffView>(`/files/${fileId}/signoffs/${id}/withdraw`);
  return data;
}

export async function getMySignature(): Promise<StoredSignature> {
  const { data } = await api.get<StoredSignature>('/me/signature');
  return data;
}

export async function saveMySignature(imageDataUrl: string): Promise<StoredSignature> {
  const { data } = await api.put<StoredSignature>('/me/signature', { imageDataUrl });
  return data;
}

export async function deleteMySignature(): Promise<StoredSignature> {
  const { data } = await api.delete<StoredSignature>('/me/signature');
  return data;
}

export async function verifySignedDocument(
  reference: string,
  sha256?: string,
): Promise<SignedDocumentVerification> {
  const { data } = await api.post<SignedDocumentVerification>('/public/signed-documents/verify', {
    reference,
    ...(sha256 ? { sha256 } : {}),
  });
  return data;
}

/** SHA-256 (hex) of a file in the browser, so a copy can be checked without uploading it. */
export async function sha256OfFile(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function useFileSignoffs(fileId: string) {
  return useQuery({ queryKey: signoffKeys.file(fileId), queryFn: () => getFileSignoffs(fileId) });
}

export function useSignoffQueue() {
  return useQuery({ queryKey: signoffKeys.queue, queryFn: getSignoffQueue });
}

export function useMySignature(enabled = true) {
  return useQuery({ queryKey: signoffKeys.mySignature, queryFn: getMySignature, enabled });
}

function useInvalidate(fileId?: string) {
  const qc = useQueryClient();
  return (targetFileId?: string) => {
    const id = targetFileId ?? fileId;
    if (id) {
      void qc.invalidateQueries({ queryKey: signoffKeys.file(id) });
      // The signed copy is a new version of the document.
      void qc.invalidateQueries({ queryKey: formsKeys.forFile(id) });
    }
    void qc.invalidateQueries({ queryKey: signoffKeys.queue });
  };
}

export function useSubmitSignoff(fileId: string) {
  const invalidate = useInvalidate(fileId);
  return useMutation({
    mutationFn: ({ formId, note }: { formId: string; note?: string }) =>
      submitSignoff(fileId, formId, note),
    onSuccess: () => invalidate(),
  });
}

export function useWithdrawSignoff(fileId: string) {
  const invalidate = useInvalidate(fileId);
  return useMutation({
    mutationFn: (id: string) => withdrawSignoff(fileId, id),
    onSuccess: () => invalidate(),
  });
}

export function useDecideSignoff() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, id, dto }: { fileId: string; id: string; dto: DecideSignoffDto }) =>
      decideSignoff(fileId, id, dto),
    onSuccess: (_data, vars) => {
      invalidate(vars.fileId);
      if (vars.dto.saveSignature) void qc.invalidateQueries({ queryKey: signoffKeys.mySignature });
    },
  });
}

export function useSaveMySignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveMySignature,
    onSuccess: (data) => qc.setQueryData(signoffKeys.mySignature, data),
  });
}

export function useDeleteMySignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteMySignature,
    onSuccess: (data) => qc.setQueryData(signoffKeys.mySignature, data),
  });
}

/** The verification for one check, or idle while none was asked for. Not retried: the endpoint is throttled. */
export function useSignedDocumentVerification(
  check: { reference: string; sha256?: string } | null,
) {
  return useQuery({
    queryKey: ['signoffs', 'verify', check?.reference ?? '', check?.sha256 ?? ''] as const,
    queryFn: () => verifySignedDocument(check?.reference ?? '', check?.sha256),
    enabled: Boolean(check?.reference),
    retry: false,
    staleTime: Infinity,
  });
}
